// PartnerPortalPage — public, read-only view of a Source for the data-sender.
// 4 tabs per plan A.8.9: Overview, Events, Errors, Integration. No auth.

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { DataHubEvent } from '@datahub/shared';
import { portalSource, portalEvents, portalIntegration, PortalSourceView, PortalIntegration } from '../api';
import { TagChip } from '../lib/tag-colors';

const POLL_MS = 3000;

type TabKey = 'overview' | 'events' | 'errors' | 'integration';

export default function PartnerPortalPage() {
  const { token } = useParams<{ token: string }>();
  const [src, setSrc] = useState<PortalSourceView | null>(null);
  const [events, setEvents] = useState<DataHubEvent[]>([]);
  const [integration, setIntegration] = useState<PortalIntegration | null>(null);
  const [tab, setTab] = useState<TabKey>('overview');
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<DataHubEvent | null>(null);

  useEffect(() => {
    if (!token) return;
    portalSource(token).then(setSrc).catch(e => setError(String(e)));
    portalIntegration(token).then(setIntegration).catch(() => undefined);
  }, [token]);

  useEffect(() => {
    if (!token) return;
    const tick = async () => {
      try {
        const evs = await portalEvents(token, 50);
        setEvents(evs);
      } catch { /* ignore */ }
    };
    void tick();
    const i = window.setInterval(() => { void tick(); }, POLL_MS);
    return () => window.clearInterval(i);
  }, [token]);

  const stats = useMemo(() => {
    if (events.length === 0) return { count24h: 0, lastAt: null as string | null };
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    let count = 0;
    let last: string | null = null;
    for (const ev of events) {
      const ms = Date.parse(ev.received_at);
      if (ms >= cutoff) count++;
      if (!last || ev.received_at > last) last = ev.received_at;
    }
    return { count24h: count, lastAt: last };
  }, [events]);

  if (error) {
    return (
      <div>
        <div className="topbar">
          <div className="brand"><span className="brand-mark">DH</span><span className="brand-name">Data Hub</span></div>
        </div>
        <div className="shell">
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--ink-3)' }}>
            <div style={{ fontSize: 28, marginBottom: 12 }}>🔒</div>
            <div style={{ fontWeight: 500, color: 'var(--ink)', marginBottom: 6 }}>Portal not found</div>
            <div style={{ fontSize: 13 }}>The link is invalid or the Source has been removed.</div>
          </div>
        </div>
      </div>
    );
  }

  if (!src) {
    return (
      <div>
        <div className="topbar">
          <div className="brand"><span className="brand-mark">DH</span><span className="brand-name">Data Hub</span></div>
        </div>
        <div className="shell" style={{ color: 'var(--ink-3)' }}>Loading…</div>
      </div>
    );
  }

  return (
    <div>
      <div className="topbar">
        <div className="brand">
          <span className="brand-mark">DH</span>
          <span className="brand-name">Data Hub</span>
          <span className="brand-env">{src.name}</span>
        </div>
        <div className="live"><span className="live-dot" />live · public</div>
      </div>

      <div className="shell">
        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--line)' }}>
          {(['overview', 'events', 'errors', 'integration'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: '10px 20px', background: 'transparent', border: 'none', cursor: 'pointer',
              fontFamily: 'var(--ui)', fontSize: 13, fontWeight: 500,
              color: tab === t ? 'var(--ink)' : 'var(--ink-3)',
              borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid transparent',
              marginBottom: -1, textTransform: 'capitalize',
            }}>{t}</button>
          ))}
        </div>

        {tab === 'overview' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
            <Kpi label="events · 24h" value={stats.count24h} />
            <Kpi label="last event" value={stats.lastAt ? timeAgo(stats.lastAt) : '—'} />
            <Kpi label="success rate" value="100%" muted />
            <Kpi label="active inputs" value={src.input_count} />
          </div>
        )}

        {tab === 'events' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="stream-block" style={{ maxHeight: 600, overflowY: 'auto' }}>
              <div className="stream-head">
                <span className="stream-pulse" />
                <strong style={{ fontSize: 14 }}>Recent events</strong>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--rail-ink-2)' }}>last {events.length}</span>
              </div>
              {events.length === 0 ? (
                <div style={{ padding: '40px 22px', textAlign: 'center', color: 'var(--rail-ink-2)' }}>
                  <div style={{ fontSize: 28, opacity: 0.5, marginBottom: 12 }}>⏳</div>
                  <div style={{ color: 'var(--rail-ink)' }}>No events yet — send a request to your webhook.</div>
                </div>
              ) : events.map(ev => (
                <div key={ev.event_id} className="event-row" style={{ cursor: 'pointer', background: selected?.event_id === ev.event_id ? 'oklch(0.22 0.01 250)' : undefined }} onClick={() => setSelected(ev)}>
                  <span className="event-time">{new Date(ev.received_at).toLocaleTimeString()}</span>
                  <span className="event-method">POST</span>
                  <span className="event-source"><span className="event-source-icon" />{ev.origin.id}</span>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--rail-ink-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {JSON.stringify(ev.payload).slice(0, 100)}
                  </span>
                </div>
              ))}
            </div>

            <div style={{ background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 14, padding: 18, maxHeight: 600, overflowY: 'auto' }}>
              {!selected ? (
                <div style={{ color: 'var(--ink-3)', textAlign: 'center', padding: 60 }}>
                  Click an event on the left to inspect it.
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 11.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.14em', color: 'var(--ink-3)', marginBottom: 8 }}>event detail</div>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 12, marginBottom: 14 }}>
                    <div><span style={{ color: 'var(--ink-3)' }}>id: </span>{selected.event_id}</div>
                    <div><span style={{ color: 'var(--ink-3)' }}>received: </span>{new Date(selected.received_at).toLocaleString()}</div>
                    <div><span style={{ color: 'var(--ink-3)' }}>origin: </span>{selected.origin.kind}/{selected.origin.id}{selected.origin.trigger_version ? ` v${selected.origin.trigger_version}` : ''}</div>
                    {selected.parent_event_id && <div><span style={{ color: 'var(--ink-3)' }}>parent: </span>{selected.parent_event_id}</div>}
                    <div><span style={{ color: 'var(--ink-3)' }}>depth: </span>{selected.derivation_depth}</div>
                  </div>
                  <pre className="code">{JSON.stringify(selected.payload, null, 2)}</pre>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'errors' && (
          <div style={{ background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 10, padding: 60, textAlign: 'center', color: 'var(--ink-3)' }}>
            <div style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 12, color: 'var(--accent)' }}>all clear</div>
            <div style={{ fontWeight: 500, color: 'var(--ink)', marginBottom: 6 }}>No errors</div>
            <div style={{ fontSize: 13 }}>All recent events accepted successfully.</div>
          </div>
        )}

        {tab === 'integration' && integration && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Multi-hook list when the source has named hooks; falls
                back to single-URL hero for sources still on default. */}
            {src?.hooks && src.hooks.length > 1 ? (
              <div className="url-hero" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div className="url-hero-eyebrow"><span>your endpoints · {src.hooks.length} webhooks</span></div>
                {src.hooks.map(h => (
                  <div key={h.input_id} style={{ borderTop: '1px solid var(--line)', paddingTop: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                      <strong style={{ fontSize: 14 }}>{h.name ?? `Hook ${h.input_id.slice(-6)}`}</strong>
                      {h.tags && Object.entries(h.tags).map(([k, v]) => (
                        <TagChip key={k} k={k} v={v} compact />
                      ))}
                    </div>
                    <div className="big-url" style={{ fontSize: 12 }}>
                      <span>{h.url}</span>
                      <button className="btn sm" style={{ marginLeft: 10 }} onClick={() => navigator.clipboard.writeText(h.url)}>copy</button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="url-hero">
                <div className="url-hero-eyebrow"><span>your endpoint · webhook</span></div>
                <div className="big-url">
                  <span>{integration.webhook_url}</span>
                  <button className="btn sm" style={{ marginLeft: 10 }} onClick={() => navigator.clipboard.writeText(integration.webhook_url)}>copy</button>
                </div>
              </div>
            )}

            <div className="collapse" data-open="true">
              <div className="collapse-head">
                <div>
                  <div className="collapse-title">Send a test event</div>
                  <div className="collapse-sub">curl example — copy + run</div>
                </div>
              </div>
              <div className="collapse-body">
                <pre className="code">{`curl -X POST '${integration.webhook_url}' \\
  -H 'Content-Type: application/json' \\
  -d '{"event": "first", "amount": 42, "name": "test"}'`}</pre>
              </div>
            </div>

            <div className="collapse" data-open="true">
              <div className="collapse-head">
                <div>
                  <div className="collapse-title">Signature (optional)</div>
                  <div className="collapse-sub">{integration.hmac.algorithm} · header {integration.hmac.header}</div>
                </div>
              </div>
              <div className="collapse-body">
                <div style={{ fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.55 }}>
                  {integration.hmac.hint}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--line)', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)', textAlign: 'center' }}>
          Powered by <a href="/" style={{ color: 'var(--accent)', textDecoration: 'none' }}>Data Hub</a>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, muted }: { label: string; value: string | number; muted?: boolean }) {
  return (
    <div style={{ background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 10, padding: 18 }}>
      <div style={{ fontSize: 10.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.14em', color: 'var(--ink-3)', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 600, color: muted ? 'var(--ink-3)' : 'var(--ink)' }}>{value}</div>
    </div>
  );
}

function timeAgo(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}
